import React, { useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  IconButton,
  Stack,
  Box
} from '@mui/material';
import { AddCircle, RemoveCircle } from '@mui/icons-material';

// Amplify GraphQL imports
import { generateClient } from 'aws-amplify/api';
import * as mutations from '../../graphql/mutations'; 

const client = generateClient({authMode: 'userPool'});

function ProspectPull() {
  const [accounts, setAccounts] = useState(['']);
  const [submissionStatus, setSubmissionStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleAddField = () => {
    setAccounts((prev) => [...prev, '']);
  };

  const handleRemoveField = (index) => {
    setAccounts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, value) => {
    setAccounts((prev) => {
      const newAccounts = [...prev];
      newAccounts[index] = value;
      return newAccounts;
    });
  };

  const handleSubmit = async () => {
    // Validation: check if all fields are blank
    const allBlank = accounts.every((account) => account.trim() === '');
    if (allBlank) {
      setErrorMessage('Please enter at least one account number before submitting.');
      // Clear success message if there was a previous successful submission
      setSubmissionStatus(false);
      return;
    }

    // If valid, clear any previous error and proceed
    setErrorMessage('');
    console.log('Accounts to submit:', accounts);

    //  Cleanse the account entries
    //    - remove '-' and ',' 
    //    - trim whitespace
    const sanitizedAccounts = accounts.map((acc) => 
        acc.replace(/[-,]/g, '').trim()
      );

      // Build the input object to match our Claim schema
      const claimInput = {
        uniqueID: 'prospect',              // default entry
        claimID: 'prospect',   // required for schema
        activeStatus: 'N',     // required for schema
        claimStatus: 'N/A',    // required for schema
        retroMember: 'prospect', // required for schema
  
        // Store the accounts in the "account" field
        account: sanitizedAccounts.join(',')
      };

      try {
        // Attempt updateClaim
        const result = await client.graphql({
          query: mutations.updateClaim,
          variables: { input: claimInput}      
      });
        setSubmissionStatus(true);
        console.log('DynamoDB updateClaim result:', result);
      } catch (error) {
        if (error.errors?.[0]?.errorType === 'DynamoDB:ConditionalCheckFailedException') {
          // Record doesn't exist, so create it
          const createResult = await client.graphql({
                   query: mutations.createClaim,
                    variables: { input: claimInput }
        });
          setSubmissionStatus(true);
          console.log('DynamoDB createClaim result:', createResult);
        } else {
          throw error; // Some other error
        }
      }
    };

  

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Please enter prospect account numbers
      </Typography>

      {accounts.map((account, index) => (
        <Stack
          key={index}
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: 2 }}
        >
          <TextField
            fullWidth
            label={`Account #${index + 1}`}
            variant="outlined"
            value={account}
            onChange={(e) => handleChange(index, e.target.value)}
          />
          {index > 0 && (
            <IconButton
              color="error"
              onClick={() => handleRemoveField(index)}
              aria-label="remove field"
            >
              <RemoveCircle />
            </IconButton>
          )}
        </Stack>
      ))}

      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AddCircle />}
          onClick={handleAddField}
        >
          Add another account
        </Button>
      </Box>

      <Button
        variant="contained"
        onClick={handleSubmit}
        sx={{ mb: 2 }}
      >
        Submit
      </Button>

      {/* Error Message (if any) */}
      {errorMessage && (
        <Typography variant="body1" color="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
      )}

      {/* Success Message */}
      {submissionStatus && (
        <Typography variant="body1" color="success.main">
          Accounts have been submitted for processing
        </Typography>
      )}
    </Container>
  );
}

export default ProspectPull;
